import React, { ReactElement, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { initialForgotPasswordFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Box } from '@mui/system';
import { useQuery } from 'hooks/useQuery';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { usePassword } from 'models/passwordContext';
import { changeForgotPasswordRecaptcha } from 'requests/auth';
import { colors } from 'theme/colors';
import useForgotPasswordFormSchema from 'pages/ForgotPasswordPage/schemaValidation';
import { useNavigate } from 'react-router-dom';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';
import { useTranslation } from 'react-i18next';

const ForgotPasswordPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const type: string = query.get('type') ?? '';
  const [formStep, setFormStep] = useState(1);
  const { schema } = useForgotPasswordFormSchema();
  const [recaptchaError, setRecaptchaError] = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isRecaptchaVerifiedSuccessfully, setIsRecaptchaVerifiedSuccessfully] =
    useState<boolean>(true); // FALSE // TO DO

  useEffect(() => setFormStep(1), []);

  useEffect(() => {
    (async function handleRecaptchaValidation() {
      if (recaptchaToken && !isRecaptchaVerifiedSuccessfully) {
        setRecaptchaError('');
        const res = await changeForgotPasswordRecaptcha(recaptchaToken);
        if (res && Object.prototype.hasOwnProperty.call(res, 'success')) {
          setIsRecaptchaVerifiedSuccessfully(res.success);
        }
      }
    })();
  }, [recaptchaToken]);

  const toggleCaptchaBadge = (show: boolean) => {
    const badge = document.getElementsByClassName('grecaptcha-badge')[0];
    if (badge && badge instanceof HTMLElement) {
      badge.style.visibility = show ? 'visible' : 'hidden';
    }
  };

  useEffect(() => {
    toggleCaptchaBadge(true);
    return () => toggleCaptchaBadge(false);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialForgotPasswordFormValues,
    resolver: yupResolver(schema),
  });
  const { forgotPassword, isForgotPasswordLoading } = usePassword();

  const onSubmit = handleSubmit(async (data: { email: string }) => {
    if ((formStep === 1 && isRecaptchaVerifiedSuccessfully) || formStep === 2) {
      forgotPassword({ ...data, type }, () => setFormStep(2));
    } else {
      setRecaptchaError('Please verify that you are not a robot');
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangeRecaptchaToken = (token: string) => {
    if (!recaptchaToken) {
      setRecaptchaToken(token);
    }
  };

  const renderForgotPasswordFormStep = (step: number) => {
    if (step === 1) {
      return (
        <Grid item container xs={12} gap={7}>
          <Grid item container xs={12} gap={1}>
            <Grid item xs={12}>
              <StyledPageTitle variant="h4">{t('page.forgotPassword.title')}</StyledPageTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2">
                {t('page.forgotPassword.subtitle')}
              </StyledPageSubTitle>
            </Grid>
          </Grid>

          <Grid item container xs={12} gap={3}>
            <Grid item xs={12} gap={2}>
              <Grid item xs={12}>
                <FormInput
                  name="email"
                  label={t('page.forgotPassword.input.email')}
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {recaptchaError && (
              <Box sx={{ minHeight: '24px' }}>
                <Typography variant="caption" color={colors.error700}>
                  {recaptchaError}
                </Typography>
              </Box>
            )}

            <Grid item container xs={12} gap={1.3} justifyItems="center">
              <Grid item xs={12}>
                <Button
                  label={t('page.signin.button.reset')}
                  onClick={() => onSubmit()}
                  isLoading={isForgotPasswordLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="start-page"
                  label={t('page.signin.button.backToStart')}
                  variant={EButtonVariants.text}
                  isLoading={isForgotPasswordLoading}
                  fullWidth
                  onClick={() => navigate(`${ERouteLinks.Auth}`)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    if (step === 2) {
      return (
        <Grid item container xs={12} gap={7}>
          <Grid item container xs={12} gap={1}>
            <Grid item xs={12}>
              <StyledPageTitle variant="h4">{t('page.forgotPassword.title')}</StyledPageTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2">
                {t('page.forgotPassword.subtitle2')}
              </StyledPageSubTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2" sx={{ fontWeight: 700 }}>
                {t('page.forgotPassword.subtitle3')}
              </StyledPageSubTitle>
            </Grid>
          </Grid>

          <Grid item container xs={12} gap={3}>
            <Grid item container xs={12} gap={1.3} justifyItems="center">
              <Grid item xs={12}>
                <Button
                  label={t('page.signin.button.resend')}
                  onClick={() => onSubmit()}
                  isLoading={isForgotPasswordLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="start-page"
                  label={t('page.signin.button.backToStart')}
                  variant={EButtonVariants.text}
                  isLoading={isForgotPasswordLoading}
                  fullWidth
                  onClick={() => navigate(`${ERouteLinks.Auth}`)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  return (
    <AuthTemplate>
      {/* <GoogleReCaptcha onVerify={handleChangeRecaptchaToken} /> */}
      {renderForgotPasswordFormStep(formStep)}
    </AuthTemplate>
  );
};
export { ForgotPasswordPage };
